import React, {useEffect, useLayoutEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

import DateTimePicker from "common/src/components/widget/DateTimePicker";
import {Card} from "common/src/components/widget/Card";
import Select from "common/src/components/widget/Select";
import OverloadsByType from "./main/OverloadsByType";
import OverloadTrendChart from "./main/OverloadTrendChart";
import VehicleTotalsChart from "./main/VehicleTotalsChart";


import {selectSiteOptions} from "../../../state/api/sitesSelectors";
import GeoLocations from "./main/GeoLocations";
import OverloadsBy from "./main/OverloadsBy";
import {selectDataMode, selectDataModeMeta, setMode} from "../../../state/slice/dataModeSlice";
import {fetchVehicleSiteDailyAggregationsActive} from "../../../state/facade/statisticsFacade";

import {fetchSiteDirectionsMetadataActive, fetchSitesActive} from "../../../state/facade/dataFacade";
import RadioGroup from "common/src/components/widget/RadioGroup";
import RadioButton from "common/src/components/widget/RadioButton";

/**
 * Helper to get first and last day of previous month.
 */
function getPreviousMonthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from, to };
}

const Dashboards = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mode = useSelector(selectDataMode);
  const { canToggle, available } = useSelector(selectDataModeMeta);

  const siteOptions = useSelector(selectSiteOptions);

  const [filteredSiteOptions, setFilteredSiteOptions] = useState(siteOptions);
  const { from: defaultFrom, to: defaultTo } = useMemo(getPreviousMonthRange, []);
  const [filterDateFrom, setFilterDateFrom] = useState(() => defaultFrom);
  const [filterDateTo, setFilterDateTo] = useState(defaultTo);
  const [activeTab, setActiveTab] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState();

  useEffect(() => {
    setFilteredSiteOptions(siteOptions);
  }, [siteOptions]);

  const sitesKey = useMemo(
      () => (selectedLocations ? selectedLocations.join(" | ") : "ALL"),
      [selectedLocations]
  );

  useEffect(() => {
    dispatch(fetchSitesActive());
  }, [mode]);

  useEffect(() => {
    dispatch(fetchVehicleSiteDailyAggregationsActive({
      from: filterDateFrom,
      to: filterDateTo,
      sites: [...(selectedLocations || [])]
    }));
  }, [dispatch, filterDateFrom, filterDateTo, selectedLocations, sitesKey, mode]);

  useEffect(() => {
    dispatch(fetchSiteDirectionsMetadataActive({
      from: filterDateFrom,
      to: filterDateTo
    }));
  }, [filterDateFrom, filterDateTo, mode]);


  const tabs = useMemo(() => [
    {
      id: "PKI1",
      label: t("dashboards.tabs.PKI1"),
      component: () => (
          <>

            <div className="card">
              <header className="card-header">
                <p className="card-header-title">
                  <span className="icon fz fz-van-shuttle medium mr-1" />
                  <span>{t('dashboards.overload_rates.title')}</span>
                </p>
              </header>
              <div className="card-content">

                <OverloadsByType />
              </div>
            </div>

            <div className="card">
              <header className="card-header">
                <p className="card-header-title">
                  <span className="icon fz fz-van-shuttle medium mr-1" />
                  <span>{t('dashboards.measurements_total.title')}</span>
                </p>
              </header>
              <div className="card-content">
                <VehicleTotalsChart includeDirection={true} />
              </div>
            </div>

            <div className="card">
              <header className="card-header">
                <p className="card-header-title">
                  <span className="icon fz fz-van-shuttle medium mr-1" />
                  <span>{t('dashboards.overload_rates_direction.title')}</span>
                </p>
              </header>
              <div className="card-content">
                <OverloadsBy fixedGroupMode={"direction"} fixedValueMode={"ratio"}/>
              </div>
            </div>
          </>
      )
    },
    {
      id: "PKI2",
      label: t("dashboards.tabs.PKI2"),
      component: () => (
          <>
            <div className="card">
              <header className="card-header">
                <p className="card-header-title">
                  <span className="icon fz fz-van-shuttle medium mr-1" />
                  <span>{t('dashboards_view.traffic_load_trend')}</span>
                </p>
              </header>
              <div className="card-content">
                <OverloadTrendChart />
              </div>
            </div>

            <div className="card">
              <header className="card-header">
                <p className="card-header-title">
                  <span className="icon fz fz-van-shuttle medium mr-1" />
                  <span>{t('dashboards_view.load_and_overload')}</span>
                </p>
              </header>
              <div className="card-content">
                <OverloadsBy />

              </div>
            </div>
          </>
      )
    },
    {
      id: "PKI3",
      label: t("dashboards.tabs.PKI3"),
      component: () => (
          <Card>
            <GeoLocations />
          </Card>
      )
    },
  ], [t]);

  useLayoutEffect(() => {
    if (!i18n.isInitialized || tabs.length === 0) return;
    const hash = window.location.hash?.replace("#", "");
    const validTab = tabs.find(t => t.id.toLowerCase() === hash?.toLowerCase());
    setActiveTab(validTab?.id ?? tabs[0].id);
  }, [tabs, i18n.isInitialized]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash?.replace("#", "");
      const validTab = tabs.find(t => t.id.toLowerCase() === hash?.toLowerCase());
      if (validTab) setActiveTab(validTab.id);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [tabs]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`);
  };

  return (
      <>
        <div className="card">
          <header className="card-header">
            <p className="card-header-title">
              <span className="icon fz fz-van-shuttle medium mr-1" />
              <span>{t('dashboards.dashboards')}</span>
            </p>
          </header>
          <div className="card-content">
            <div className="columns">
              <div className="column is-one-half is-full-mobile">
                <Select
                    options={filteredSiteOptions || []}
                    label={t("dashboards.site_location_select")}
                    isMultiSelect={true}
    onSearchQueryChange={(filter) => setFilteredSiteOptions(siteOptions.filter((it)=> it.label.toLowerCase().includes(filter.toLowerCase())))}
                     selectedItem={selectedLocations}
                    onChange={setSelectedLocations}
                    // use your custom option resolvers (your Select supports custom label/value)
                    optionsLabel={(o) => o.label}
                    optionsValue={(o) => o.value}
                />
              </div>

              <div className="column is-one-quarter is-full-mobile">
                <DateTimePicker
                    type="date"
                    label={t("dashboards.filter_date_from")}
                    value={filterDateFrom}
                    onChange={setFilterDateFrom}
                />
              </div>
              <div className="column is-one-quarter is-full-mobile">
                <DateTimePicker
                    type="date"
                    label={t("dashboards.filter_date_to")}
                    value={filterDateTo}
                    onChange={setFilterDateTo}
                />
              </div>
              </div>
              <div className="columns">

              <div className="column is-one-half is-full-mobile"></div>
                <div className="field is-grouped is-align-items-center mb-3">

            <div className="column is-one-half is-full-mobile">
                    <div className="field is-grouped is-align-items-center mb-3">

                      {canToggle && (
                          <RadioGroup
                              value={mode}
                              onChange={(arg) => {
                                const next =
                                    typeof arg === "string" ? arg : arg?.target?.id; // supports value or event
                                if (next) dispatch(setMode(next));
                              }}
                          >
                            <RadioButton id="processed" label={t("dashboards.mode.processed")} type="circle" className="mr-4" />
                            <RadioButton id="raw" label={t("dashboards.mode.raw")} type="circle" />
                          </RadioGroup>
                      )}
                      </div>
                    </div>
                  </div>

                </div>

          </div>
        </div>

        <div className="tabs is-boxed">
          <ul>
            {tabs.map(tab => (
                <li key={tab.id} className={activeTab === tab.id ? "is-active" : ""}>
                  <a onClick={() => handleTabClick(tab.id)}>
                    {tab.label}
                  </a>
                </li>
            ))}
          </ul>
        </div>

        {activeTab && tabs.map(tab => (
            tab.id === activeTab && (
                <div key={tab.id} className="mb-4">
                  {tab.component()}
                </div>
            )
        ))}
      </>
  );
};



export default Dashboards;
